import { DatePicker, Input, Form, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import CalendarIcon from 'assets/icons/calendar';
import FileUploader from 'components/features/file-uploader';
import { EFileStatus } from 'components/features/file-uploader/enum';
import {
  fields,
  getValidationSchema,
} from 'components/features/submission-registration/form';
import FormActionButtons from 'components/ui/form-action-buttons';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormSection from 'components/ui/form-section';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import { SortedSelect } from 'components/ui/sorted-select';
import { ECountries } from 'constants/enums';
import { EQueryKeys } from 'constants/query-keys';
import dayjs from 'dayjs';
import { useFileSession } from 'hooks/useFileSession';
import { t } from 'i18next';
import {
  CreateSubmissionPayloadDtoPolicyTypeEnum,
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  SubmissionResponseDto,
  UpdateSubmissionPayloadDto,
} from 'libs/pinnacle-sdk';
import { debounce, isEqual, uniqBy } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { SubmissionTabProps } from 'routes/Submissions/SubmissionDetail/navigation-tab/submission-config-tabs';
import { referenceDataApi, submissionApi, userApi } from 'services/apis';
import { getLocalDateForMidnightUTC } from 'utils';
import { handleError, validateForm } from 'utils/form';
import { isSubmissionEditable } from 'utils/submission';

const SubmissionRegistration: React.FC<SubmissionTabProps> = ({
  submission,
  includeBack,
  includeNext,
  navigateNext,
}) => {
  const queryClient = useQueryClient();

  const initialValues = useMemo(
    () => ({
      assignedHandlerId: submission.assignedHandlerId,
      namedUnderwriterId: submission.namedUnderwriterId,
      originalCurrency: submission.originalCurrency,
      inceptionDate: getLocalDateForMidnightUTC(submission.inceptionDate),
      expiryDate: submission.expiryDate
        ? getLocalDateForMidnightUTC(submission.expiryDate)
        : new Date(),
      principalOccupation: submission.principalOccupation,
      mainRegionAndRiskTerritory: submission.mainRegionAndRiskTerritory,
      keyRiskLocation: submission.keyRiskLocation,
      comment: submission.changelogs?.[0]?.comment,
      subClass: submission.subClass,
    }),
    [submission]
  );

  const [form] = Form.useForm<UpdateSubmissionPayloadDto>();
  const [isFormValid, setIsFormValid] = useState(true);
  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submission.status);
  }, [submission.status]);

  const validateFormFields = useCallback(async () => {
    const values = form.getFieldsValue();
    const isValid = await validateForm(
      values,
      form,
      fields,
      getValidationSchema(t)
    );
    setIsFormValid(isValid);
  }, [form]);

  useEffect(() => {
    validateFormFields();
  }, [validateFormFields]);

  const {
    data: principalOccupations,
    isLoading: isLoadingPrincipalOccupations,
  } = useQuery(
    EQueryKeys.PRINCIPAL_OCCUPATIONS,
    async () => {
      return referenceDataApi.getPrincipalOccupations();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery(
    EQueryKeys.CURRENCIES,
    async () => {
      return referenceDataApi.getCurrencies();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const {
    data: mainRegionAndRiskTerritoryList,
    isLoading: isLoadingMainRegionAndRiskTerritoryList,
  } = useQuery(
    EQueryKeys.MAIN_REGION_AND_RISK_TERRITORY_LIST,
    async () => {
      return referenceDataApi.getMainRegionAndRiskTerritoryList({
        getMainRegionAndRiskTerritoryListQueryDto: {},
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: subClasses, isLoading: isLoadingSubClasses } = useQuery(
    [EQueryKeys.SUB_CLASSES, submission.lineOfBusiness],
    async () => {
      return referenceDataApi.getSubClasses({
        lineOfBusiness: submission.lineOfBusiness,
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const updateSubmissionMutation = useMutation(
    (submissionPayload: UpdateSubmissionPayloadDto) =>
      submissionApi.updateSubmission({
        submissionId: submission.submissionId,
        updateSubmissionPayloadDto: submissionPayload,
      }),
    {
      onSuccess: () => {
        message.success(t('submission.recordUpdatedSuccessfully'));
      },
      onError: async (error: { response: Response }) =>
        handleError({
          error,
          defaultMessage: t('submission.recordUpdatedFailed'),
        }),
    }
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateSubmissionMutate = useCallback(
    debounce(
      (
        submissionPayload: UpdateSubmissionPayloadDto,
        onSuccess?: (data: SubmissionResponseDto) => void,
        onError?: () => void
      ) => {
        updateSubmissionMutation.mutate(submissionPayload, {
          onSuccess,
          onError,
        });
      },
      500
    ),
    [updateSubmissionMutation]
  );

  const handleOnFinish = useCallback(() => {
    const fieldsValues = form.getFieldsValue();
    const fileIds = fileList
      .filter((file) => !!file.id)
      .map((file) => file.id) as string[];
    if (
      isEqual(fieldsValues, initialValues) &&
      isEqual(
        fileIds,
        (submission?.files ?? []).map((file) => file.fileId)
      )
    ) {
      return navigateNext();
    }

    return debouncedUpdateSubmissionMutate(
      {
        ...fieldsValues,
        comment:
          !!fieldsValues.comment &&
          initialValues.comment !== fieldsValues.comment
            ? fieldsValues.comment
            : undefined,
        fileIds,
      },
      () => {
        queryClient
          .refetchQueries([
            EQueryKeys.SUBMISSION_BY_ID,
            submission.submissionId,
          ])
          .then(() => {
            navigateNext();
          });
      }
    );
  }, [
    debouncedUpdateSubmissionMutate,
    form,
    initialValues,
    navigateNext,
    queryClient,
    submission.submissionId,
  ]);

  const { fileSessionId, fileList, setFileList } = useFileSession(
    submission?.files
  );

  return (
    <Form
      form={form}
      layout="vertical"
      className="space-y-8"
      initialValues={initialValues}
      onFinish={handleOnFinish}
      onValuesChange={validateFormFields}
    >
      <FormSection title={t('submission.submissionRegistration')}>
        <FormLayoutGroup>
          <Form.Item
            name="assignedHandlerId"
            label={t('submission.assignedHandler')}
          >
            <LazyLoadingSelect
              fetchOptions={async (page, pageSize, searchText) => {
                const { items } = await userApi.filterUsers({
                  searchText,
                  page,
                  pageSize,
                });
                return {
                  data: items.map((user) => ({
                    label: user.name || '',
                    value: user.userId,
                  })),
                };
              }}
              suffixIcon={<ArrowDownIcon />}
              additionalOptions={
                submission?.assignedHandler
                  ? [
                      {
                        label: submission.assignedHandler.name ?? '',
                        value: submission.assignedHandler.userId,
                      },
                    ]
                  : []
              }
              allowClear
              size="large"
              placeholder={t('submission.selectUnderwriter')}
              queryKey="underwriter-lazy-loading-select"
              disabled={!isEditingMode}
            />
          </Form.Item>

          <Form.Item
            name="namedUnderwriterId"
            label={t('submission.namedUnderwriter')}
          >
            <LazyLoadingSelect
              fetchOptions={async (page, pageSize, searchText) => {
                const { items } = await userApi.filterUsers({
                  searchText,
                  page,
                  pageSize,
                  isNamedUnderwriter: true,
                });
                return {
                  data: items.map((user) => ({
                    label: user.name || '',
                    value: user.userId,
                  })),
                };
              }}
              suffixIcon={<ArrowDownIcon />}
              additionalOptions={
                submission?.namedUnderwriter
                  ? [
                      {
                        label: submission.namedUnderwriter.name ?? '',
                        value: submission.namedUnderwriter.userId,
                      },
                    ]
                  : []
              }
              allowClear
              size="large"
              placeholder={t('submission.selectUnderwriter')}
              queryKey="assistant-lazy-loading-select"
              disabled={!isEditingMode}
            />
          </Form.Item>
        </FormLayoutGroup>

        <FormLayoutGroup mdColumns={1}>
          <Form.Item name="originalCurrency" label={t('general.currency')}>
            <SortedSelect
              suffixIcon={<ArrowDownIcon />}
              allowClear
              size="large"
              placeholder={t('submission.selectCurrency')}
              loading={isLoadingCurrencies}
              options={(currencies || []).map((currency) => ({
                label: `${currency.name} (${currency.code})`,
                value: currency.code,
              }))}
              disabled={!isEditingMode}
              showSearch
            ></SortedSelect>
          </Form.Item>
        </FormLayoutGroup>

        <FormLayoutGroup mdColumns={2}>
          <Form.Item name="inceptionDate" label={t('submission.inceptionDate')}>
            <DatePicker
              format={'DD/MM/YYYY'}
              allowClear
              size="large"
              className="w-full"
              suffixIcon={<CalendarIcon />}
              disabled={!isEditingMode}
              onChange={(date) => {
                if (date) {
                  const currentExpiryDate = form.getFieldValue('expiryDate');
                  if (!currentExpiryDate) {
                    const expiryDate = date.clone().add(1, 'year');
                    form.setFieldValue('expiryDate', expiryDate);
                  }
                  validateFormFields();
                }
              }}
            />
          </Form.Item>

          <Form.Item name="expiryDate" label={t('submission.expiryDate')}>
            <DatePicker
              format={'DD/MM/YYYY'}
              allowClear
              size="large"
              className="w-full"
              suffixIcon={<CalendarIcon />}
              disabled={!isEditingMode}
              disabledDate={(current) => {
                const inceptionDate = form.getFieldValue('inceptionDate');
                return (
                  current &&
                  inceptionDate &&
                  current < inceptionDate.add(1, 'day')
                );
              }}
            />
          </Form.Item>
        </FormLayoutGroup>

        <FormLayoutGroup>
          <Form.Item
            name="principalOccupation"
            label={t('submission.principalOccupation')}
          >
            <SortedSelect
              suffixIcon={<ArrowDownIcon />}
              allowClear
              size="large"
              placeholder={t('submission.selectPrincipalOccupation')}
              loading={isLoadingPrincipalOccupations}
              options={(principalOccupations || []).map(
                (principalOccupation) => ({
                  label: principalOccupation.name,
                  value: principalOccupation.name,
                })
              )}
              disabled={!isEditingMode}
              showSearch
            ></SortedSelect>
          </Form.Item>

          <Form.Item name="subClass" label={t('submission.subClass')}>
            <SortedSelect
              suffixIcon={<ArrowDownIcon />}
              allowClear
              size="large"
              disabled={!submission.lineOfBusiness || !isEditingMode}
              loading={isLoadingSubClasses}
              placeholder={t('submission.selectSubClass')}
              options={(subClasses || []).map((subClass) => ({
                label: subClass.name,
                value: subClass.name,
              }))}
              showSearch
            ></SortedSelect>
          </Form.Item>
        </FormLayoutGroup>

        <FormLayoutGroup>
          <Form.Item
            name="mainRegionAndRiskTerritory"
            label={t('submission.mainRegionAndRiskTerritory')}
          >
            <SortedSelect
              suffixIcon={<ArrowDownIcon />}
              allowClear
              size="large"
              placeholder={t('submission.selectMainRegionAndRiskTerritory')}
              loading={isLoadingMainRegionAndRiskTerritoryList}
              options={uniqBy(
                (
                  (submission.policyType ===
                  CreateSubmissionPayloadDtoPolicyTypeEnum.Insurance
                    ? mainRegionAndRiskTerritoryList?.filter(
                        (item) => item.country !== ECountries.AUSTRALIA
                      )
                    : mainRegionAndRiskTerritoryList) || []
                ).map((mainRegionAndRiskTerritory) => ({
                  label: mainRegionAndRiskTerritory.country,
                  value: mainRegionAndRiskTerritory.country,
                })),
                'value'
              )}
              disabled={!isEditingMode}
              showSearch
            ></SortedSelect>
          </Form.Item>
          <Form.Item
            name="keyRiskLocation"
            label={t('submission.keyRiskLocation')}
          >
            <Input
              size="large"
              placeholder={t('submission.enterKeyRiskLocation')}
              disabled={!isEditingMode}
              autoComplete="off"
            />
          </Form.Item>
        </FormLayoutGroup>
        <FormLayoutGroup mdColumns={1}>
          <Form.Item name="comment" label={t('general.comments')}>
            <TextArea
              rows={4}
              placeholder={t('general.enter', {
                field: t('general.comments'),
              })}
              disabled={!isEditingMode}
            ></TextArea>
          </Form.Item>
        </FormLayoutGroup>
        <FormLayoutGroup mdColumns={1}>
          <Form.Item className="mt-6">
            {fileSessionId && (
              <FileUploader
                sessionId={fileSessionId}
                entityType={
                  CreateUploadedFilePayloadDtoEntityTypeEnum.Submission
                }
                fileList={fileList}
                setFileList={setFileList}
                disabled={!isEditingMode}
              />
            )}
          </Form.Item>
        </FormLayoutGroup>
      </FormSection>
      <FormActionButtons
        confirmText="Next"
        isConfirmDisabled={
          !isFormValid ||
          fileList.some((file) => file.status === EFileStatus.UPLOADING)
        }
        includeCancel={includeBack}
        includeConfirm={includeNext}
        confirmOutlined={false}
      />
    </Form>
  );
};

export default SubmissionRegistration;
